import { Component } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { map, Observable, tap } from 'rxjs';
import { Location } from '@angular/common'
import { FeedbackOption, FeedbackQuestion, SelfAssessmentService } from '../self-assessment.service';
import { environment } from 'src/environments/environment';
import { StudentFeedbackService } from '../../student -feedback/student-feedback.service';


interface AssessmentGroup {
  key: 'A' | 'B' | 'C' | 'D';
  title: string;
  rangeLabel: string;
  questions: AssessmentQuestion[];
}

interface AssessmentQuestion {
  /** Serial number within the assessment (1…n) */
  no: number;
  questionId: number;
  /** From API `questionTitle` — plain heading line */
  title: string;
  /** From API `question` — HTML body; null when empty */
  questionHtml: SafeHtml | null;
}

interface AnswerState {
  questionId: number;
  optionId: number;
  optionRank: number;
  optionText: string;
  reason?: string;
}
@Component({
  selector: 'app-self-assessment',
  templateUrl: './self-assessment.component.html',
  styleUrls: ['./self-assessment.component.scss']
})
export class SelfAssessmentComponent {
  started = false;
  /** True after successful submit — shows confirmation (not instruction page). */
  submitted = false;
  showInstruction = false;
  showSubmitConfirm = false;

  loading = true;
  errorMessage = '';
  submitError = '';
  submitting = false;
  readonlyMode = false;
  readonlyMessage = '';

  /** API categoryName */
  readonly categoryName = 'Pre-Program Leadership Self-Assessment';

  /** Rank 1/2 explanation — same copy as mobile app */
  readonly explainChoiceCopy = 'Please Explain your choice';

  staffName = '—';
  /** Profile card line 2: payroll id */
  staffPayrollId = '—';
  /** Profile card line 3: designation */
  staffDesignation = '—';
  /** Profile card line 4: campus name */
  staffCampus = '—';

  /** Object URL for profile photo (fetched with JWT; raw API URL cannot be used on img src). */
  profileImageObjectUrl: string | null = null;
  private profileImageRevokeUrl: string | null = null;

  groups: AssessmentGroup[] = [];

  groupIndex = 0;
  /** questionId -> answer */
  answers = new Map<number, AnswerState>();
  /** questionId -> reason text (only for rank 1/2) */
  reasons = new Map<number, string>();
  /** Saved selections from getPreProgramDetails (applied when options load) */
  private readonly savedSelections = new Map<
    number,
    { optionId: number; feedbackId: number; reason: string; optionText: string }
  >();

  constructor(
    private readonly api: SelfAssessmentService,
    private readonly sanitizer: DomSanitizer,
    private readonly location: Location
  ) {}

  ngOnInit(): void {
    this.loadProfile();
    this.loadQuestions();
  }

  ngOnDestroy(): void {
    this.revokeProfileImageObjectUrl();
  }

  private revokeProfileImageObjectUrl(): void {
    if (this.profileImageRevokeUrl) {
      try {
        URL.revokeObjectURL(this.profileImageRevokeUrl);
      } catch {
        /* ignore */
      }
      this.profileImageRevokeUrl = null;
    }
    this.profileImageObjectUrl = null;
  }

  private loadProfileImage(opts?: { payrollId?: string; tempPayrollId?: string }): void {
    this.revokeProfileImageObjectUrl();
    this.api.getProfileImageBlob(opts).subscribe({
      next: (blob) => {
        if (!blob?.size) return;
        if (blob.type && !blob.type.startsWith('image/')) return;
        const url = URL.createObjectURL(blob);
        this.profileImageRevokeUrl = url;
        this.profileImageObjectUrl = url;
      }
    });
  }

  private loadProfile(): void {
    const payrollId = this.api.getStoredPayrollId();
    const encryptedPayrollId = this.api.getStoredEncryptedPayrollId();
    this.api.getUserProfile(encryptedPayrollId || payrollId).subscribe({
      next: (res) => {
        const dash: any = (res as any)?.employeeDashBoardInfo ?? {};
        const basic: any = (res as any)?.basicInfo ?? {};

        const name = String(dash?.empName ?? '').trim();
        const campus = String(dash?.campusName ?? '').trim();
        const profilePayroll = String(dash?.payrollId ?? '').trim();
        const profileTemp = String(dash?.tempPayrollId ?? '').trim();
        const pid = profilePayroll || profileTemp || payrollId;

        const designation = String(basic?.designation ?? '').trim();

        if (name) this.staffName = name;
        else this.staffName = '—';

        this.staffPayrollId = pid || '—';

        const des =
          designation && designation !== '-' ? designation : '';
        this.staffDesignation = des || '—';

        this.staffCampus = campus || '—';

        this.loadProfileImage({
          payrollId: profilePayroll || profileTemp || undefined,
          tempPayrollId: profileTemp || undefined
        });
      },
      error: () => {
        // Keep defaults if profile API fails; still try image using stored payroll.
        this.loadProfileImage();
      }
    });
  }

  private loadQuestions(): void {
    this.loading = true;
    this.errorMessage = '';
    this.api.getQuestions(this.categoryName).subscribe({
      next: (list: FeedbackQuestion[]) => {
        const questions = Array.isArray(list) ? list : [];
        const mapped: AssessmentQuestion[] = questions.map((q, idx) => {
          const title = String(q.questionTitle ?? '').trim();
          const html = String(q.question ?? '').trim();
          return {
            no: idx + 1,
            questionId: Number(q.questionId),
            title,
            questionHtml: html ? this.sanitizer.bypassSecurityTrustHtml(html) : null
          };
        });
        this.groups = this.buildGroups(mapped);
        this.groupIndex = 0;
        this.loading = false;

        // After questions load, try to load saved answers for this payroll + category.
        this.loadSavedAnswers();
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Could not load questions. Please try again.';
        this.groups = [];
      }
    });
  }

  private loadSavedAnswers(): void {
    const payrollId = this.api.getStoredPayrollId();
    this.api.getPreProgramDetails(payrollId, this.categoryName).subscribe({
      next: (res: any) => {
        // Any non-empty validateMessage means editing is not allowed (cooldown, already submitted, etc.).
        const vm = String(res?.validateMessage ?? '').trim();
        this.readonlyMessage = vm;
        this.readonlyMode = !!vm;

        // If backend says "fresh" (empty validateMessage), start with a clean slate.
        if (!vm) {
          this.savedSelections.clear();
          this.answers.clear();
          this.reasons.clear();
          return;
        }

        const list =
          (res && Array.isArray(res.questionAndOption) && res.questionAndOption) ||
          (res && res.data && Array.isArray(res.data) && res.data) ||
          (Array.isArray(res) ? res : []);

        this.savedSelections.clear();
        for (const row of list) {
          const qid = Number(row?.questionId);
          const oid = Number(row?.optionId);
          if (!Number.isFinite(qid) || !Number.isFinite(oid)) continue;
          this.savedSelections.set(qid, {
            optionId: oid,
            feedbackId: Number(row?.feedbackId ?? 0) || 0,
            reason: String(row?.feedbackPopUpText ?? '').trim(),
            optionText: String(row?.feedbackOptionText ?? '')
          });
          const reason = String(row?.feedbackPopUpText ?? '').trim();
          if (reason) this.reasons.set(qid, reason);
        }
      },
      error: () => {
        // If no saved data or API fails, ignore and allow fresh submission.
        this.readonlyMode = false;
        this.readonlyMessage = '';
      }
    });
  }

  private buildGroups(all: AssessmentQuestion[]): AssessmentGroup[] {
    const slice = (startNo: number, endNo: number) =>
      all.filter((q) => q.no >= startNo && q.no <= endNo);
    const groups: AssessmentGroup[] = [
      { key: 'A', title: 'Academic Excellence & Student Outcomes', rangeLabel: '1–7', questions: slice(1, 7) },
      { key: 'B', title: 'Administrative Discipline & Operational Governance', rangeLabel: '8–17', questions: slice(8, 17) },
      { key: 'C', title: 'Campus Life, Student Well‑being & Engagement', rangeLabel: '18–24', questions: slice(18, 24) },
      { key: 'D', title: 'Leadership, Financial Awareness & Campus Growth', rangeLabel: '25–30', questions: slice(25, 30) }
    ];
    return groups.filter((g) => g.questions.length > 0);
  }

  get totalQuestions(): number {
    return this.groups.reduce((sum, g) => sum + (g.questions?.length ?? 0), 0);
  }

  get currentGroup(): AssessmentGroup {
    return this.groups[this.groupIndex]!;
  }

  get answeredCount(): number {
    return this.answers.size;
  }

  private isQuestionAnswered(questionId: number): boolean {
    return this.answers.has(Number(questionId));
  }

  private isQuestionValid(questionId: number): boolean {
    const qid = Number(questionId);
    const a = this.answers.get(qid);
    if (!a) return false;
    if (a.optionRank === 1 || a.optionRank === 2) {
      return !!String(this.reasons.get(qid) ?? '').trim();
    }
    return true;
  }

  get canGoNext(): boolean {
    if (!this.started || this.loading || this.submitting) return false;
    // In readonly mode user should still be able to navigate across sections to review.
    if (this.readonlyMode) return this.groupIndex < this.groups.length - 1;
    const qs = this.currentGroup?.questions ?? [];
    if (qs.length === 0) return false;
    // Same as mobile: every question in this section must be fully answered (option + rank 1/2 explanation).
    return qs.every((q) => this.isQuestionValid(q.questionId));
  }

  get canSubmit(): boolean {
    if (!this.started || this.loading || this.readonlyMode || this.submitting) return false;
    const allQs = this.groups.flatMap((g) => g.questions ?? []);
    if (allQs.length === 0) return false;
    return allQs.every((q) => this.isQuestionValid(q.questionId));
  }

  get submitDisabledHint(): string {
    if (!this.started || this.loading) return '';
    if (this.readonlyMode) return this.readonlyMessage || '';
    if (this.submitting) return 'Saving…';

    const allQs = this.groups.flatMap((g) => g.questions ?? []);
    if (allQs.length === 0) return '';

    const missingAnswers = allQs.filter((q) => !this.isQuestionAnswered(q.questionId)).length;
    if (missingAnswers > 0) return `Answer remaining questions: ${missingAnswers}`;

    const missingReasons = allQs.filter((q) => {
      const a = this.answers.get(Number(q.questionId));
      if (!a) return false;
      if (a.optionRank !== 1 && a.optionRank !== 2) return false;
      return !String(this.reasons.get(Number(q.questionId)) ?? '').trim();
    }).length;
    if (missingReasons > 0) {
      return `${this.explainChoiceCopy} for ${missingReasons} question(s).`;
    }

    return '';
  }

  /** Why Next is disabled on non-final sections (mirrors mobile: all mandatory before advancing). */
  get nextDisabledHint(): string {
    if (!this.started || this.loading || this.readonlyMode || this.submitting) return '';
    if (this.groupIndex >= this.groups.length - 1) return '';
    const qs = this.currentGroup?.questions ?? [];
    if (qs.length === 0) return '';

    const missingAnswers = qs.filter((q) => !this.isQuestionAnswered(q.questionId)).length;
    if (missingAnswers > 0) {
      return `Answer all questions in this section (${missingAnswers} remaining).`;
    }

    const missingReasons = qs.filter((q) => {
      const a = this.answers.get(Number(q.questionId));
      if (!a) return false;
      if (a.optionRank !== 1 && a.optionRank !== 2) return false;
      return !String(this.reasons.get(Number(q.questionId)) ?? '').trim();
    }).length;
    if (missingReasons > 0) {
      return `${this.explainChoiceCopy} for ${missingReasons} question(s) in this section.`;
    }

    return '';
  }

  get canGoBack(): boolean {
    if (!this.started || this.loading || this.submitting) return false;
    // Back navigation should still work in readonly mode (viewing submitted answers).
    return true;
  }

  get progressText(): string {
    const firstNo = this.currentGroup?.questions?.[0]?.no ?? 1;
    return `${firstNo}/${Math.max(1, this.totalQuestions)}`;
  }

  get progressPercent(): number {
    if (this.totalQuestions <= 0) return 0;
    return Math.round((this.answeredCount / this.totalQuestions) * 100);
  }

  selectedRank(questionId: number): number | null {
    const a = this.answers.get(questionId);
    return a?.optionRank ?? null;
  }

  setOption(questionId: number, opt: FeedbackOption): void {
    if (this.readonlyMode) return;
    if (!opt) return;
    this.answers.set(questionId, {
      questionId,
      optionId: opt.optionId,
      optionRank: opt.optionRank,
      optionText: String(opt.optionName ?? ''),
      reason: this.reasons.get(questionId) ?? undefined
    });
    // If not rank 1/2, clear any reason.
    if (opt.optionRank !== 1 && opt.optionRank !== 2) {
      this.reasons.delete(questionId);
    }
  }

  needsReason(questionId: number): boolean {
    const rank = this.selectedRank(questionId);
    return rank === 1 || rank === 2;
  }

  setReason(questionId: number, value: string): void {
    if (this.readonlyMode) return;
    const v = (value ?? '').toString();
    this.reasons.set(questionId, v);
    const a = this.answers.get(questionId);
    if (a) {
      a.reason = v;
      this.answers.set(questionId, a);
    }
  }

  private scrollToTop(): void {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  }

  private focusReasonField(questionId: number): void {
    const qid = Number(questionId);
    // Allow DOM to update before focusing.
    setTimeout(() => {
      const el = document.getElementById(`reason-${qid}`) as HTMLTextAreaElement | null;
      const wrap = document.getElementById(`q-${qid}`) as HTMLElement | null;
      (wrap || el)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' } as any);
      el?.focus?.();
    }, 0);
  }

  options$(questionId: number): Observable<FeedbackOption[]> {
    return this.api.getOptionsCached(questionId).pipe(
      map((opts) =>
        [...(opts ?? [])].sort((a, b) => Number(a.optionRank ?? 0) - Number(b.optionRank ?? 0))
      ),
      tap((opts) => {
        // Apply saved optionId -> optionRank once options for this question are available.
        if (this.answers.has(questionId)) return;
        const saved = this.savedSelections.get(questionId);
        if (!saved) return;
        const match = opts.find((o) => Number(o.optionId) === Number(saved.optionId));
        if (!match) return;
        this.answers.set(questionId, {
          questionId,
          optionId: match.optionId,
          optionRank: match.optionRank,
          optionText: String(match.optionName ?? saved.optionText ?? ''),
          reason: this.reasons.get(questionId) ?? saved.reason ?? undefined
        });
      })
    );
  }

  start(): void {
    this.submitted = false;
    this.started = true;
    this.groupIndex = 0;
    this.showInstruction = false;
  }

  confirmationBack(): void {
    this.location.back();
  }

  back(): void {
    if (this.groupIndex <= 0) {
      this.started = false;
      return;
    }
    this.groupIndex -= 1;
    this.scrollToTop();
  }

  next(): void {
    if (!this.canGoNext) return;
    if (this.groupIndex >= this.groups.length - 1) return;
    this.groupIndex += 1;
    this.scrollToTop();
  }

  submit(): void {
    if (this.readonlyMode) return;
    this.submitError = '';
    if (!this.canSubmit) return;

    const total = this.totalQuestions;
    if (total <= 0) {
      this.submitError = 'No questions to submit.';
      return;
    }

    // Validate: all answered
    if (this.answers.size !== total) {
      this.submitError = `Please answer all questions (${this.answers.size}/${total}).`;
      return;
    }

    // Validate: reason required for rank 1/2 and must be non-empty
    for (const [questionId, a] of this.answers.entries()) {
      if ((a.optionRank === 1 || a.optionRank === 2) && !(this.reasons.get(questionId) || '').trim()) {
        this.submitError = `${this.explainChoiceCopy}.`;
        this.focusReasonField(questionId);
        return;
      }
    }

    this.showSubmitConfirm = true;
  }

  cancelSubmitConfirm(): void {
    this.showSubmitConfirm = false;
  }

  confirmSubmit(): void {
    this.showSubmitConfirm = false;
    if (this.submitting) return;

    const payrollId = this.api.getStoredPayrollId();
    const payload = {
      payrollId,
      questionAndOption: Array.from(this.answers.values()).map((a) => {
        const qid = Number(a.questionId);
        const reason = String((this.reasons.get(qid) ?? a.reason ?? '').toString()).trim();
        const isReasonRank = a.optionRank === 1 || a.optionRank === 2;
        const savedFeedbackId = this.savedSelections.get(qid)?.feedbackId ?? 0;
        return {
          questionId: qid,
          optionId: Number(a.optionId),
          // Send selected option text for backend reporting/auditing.
          feedbackOptionText: String(a.optionText ?? '').trim(),
          // When re-saving, backend requires feedbackId to update instead of creating stale rows.
          feedbackId: Number(savedFeedbackId) || 0,
          feedbackPopUpText: isReasonRank ? reason : ''
        };
      }),
      feedBackType: 'EMPLOYEE_FEEDBACK',
      catagory: this.categoryName,
      validateMessage: ''
    };

    this.submitting = true;
    this.api.savePreProgramDetails(payload).subscribe({
      next: () => {
        this.submitting = false;
        this.showInstruction = false;
        this.started = false;
        this.submitted = true;
        this.scrollToTop();
      },
      error: () => {
        this.submitting = false;
        this.submitError = 'Save failed. Please try again.';
      }
    });
  }

  trackByIndex = (index: number): number => index;
  trackByQuestionId = (_: number, q: AssessmentQuestion): number => q.questionId;
  trackByOptionId = (_: number, o: FeedbackOption): number => o.optionId;
}

